import React, { useEffect, useRef, useState } from 'react';
import { Button, ButtonGroup, DateRangePicker, Form, InputNumber, Message, SelectPicker, Toggle, Tooltip, Whisper } from 'rsuite';

const DEFAULT_REQUEST_TYPE = 'time off';

const Textarea = React.forwardRef(function Textarea(
  { value, onChange, className, ...rest },
  ref
) {
  return (
    <textarea
      ref={ref}
      value={value ?? ''}
      onChange={(e) => onChange?.(e.target.value, e)}
      className={['rs-input', className].filter(Boolean).join(' ')}
      {...rest}
    />
  );
});

const coerceFiniteNumber = (value, fallback) => {
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? n : fallback;
};

const clamp = (n, min, max) => Math.min(max, Math.max(min, n));

const WORKDAY_HOURS = 8;

const isSameDay = (a, b) => {
  if (!a || !b) return false;
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
};

const isWeekday = (d) => {
  const day = d.getDay();
  return day >= 1 && day <= 5;
};

const makeTime = (hours, minutes = 0) => {
  const dt = new Date(0);
  dt.setHours(hours, minutes, 0, 0);
  return dt;
};

const minutesSinceMidnight = (d) => {
  if (!d) return null;
  return d.getHours() * 60 + d.getMinutes();
};

const isInvalidTimeRange = (startTime, endTime) => {
  const startMinutes = minutesSinceMidnight(startTime);
  const endMinutes = minutesSinceMidnight(endTime);
  if (startMinutes == null || endMinutes == null) return false;
  return endMinutes <= startMinutes;
};


const diffHours = (startTime, endTime) => {
  if (!startTime || !endTime) return null;
  const ms = endTime.getTime() - startTime.getTime();
  if (!Number.isFinite(ms)) return null;
  return ms / (1000 * 60 * 60);
};

const snapMinutesToQuarterHour = (time) => {
  if (!time) return time;
  const snapped = new Date(time);
  const minutes = snapped.getMinutes();
  const snappedMinutes = Math.round(minutes / 15) * 15;
  if (snappedMinutes === 60) {
    snapped.setHours(snapped.getHours() + 1, 0, 0, 0);
  } else {
    snapped.setMinutes(snappedMinutes, 0, 0);
  }
  return snapped;
};

const getTimeParts12h = (time) => {
  const date = time || makeTime(9, 0);
  const hours24 = date.getHours();
  const minutes = date.getMinutes();
  const meridiem = hours24 >= 12 ? 'PM' : 'AM';
  let hour12 = hours24 % 12;
  if (hour12 === 0) hour12 = 12;
  return { hour12, minutes, meridiem };
};

const to24Hour = (hour12, meridiem) => {
  const h = Number(hour12);
  if (!Number.isFinite(h) || h < 1 || h > 12) return 0;
  if (meridiem === 'AM') return h === 12 ? 0 : h;
  return h === 12 ? 12 : h + 12;
};

const setTimeParts = (prevTime, { hour12, minutes, meridiem }) => {
  const base = prevTime ? new Date(prevTime) : makeTime(9, 0);
  base.setHours(to24Hour(hour12, meridiem), minutes, 0, 0);
  return base;
};

const timeFromMinutes = (minutes) => {
  if (minutes == null) return null;
  const m = Number(minutes);
  if (!Number.isInteger(m) || m < 0 || m > 1439) return null;
  const hours = Math.floor(m / 60);
  const mins = m % 60;
  return makeTime(hours, mins);
};

const calculateTotalWorkHours = ({ range, includeHours, startTime, endTime }) => {
  if (!range || !range[0] || !range[1]) return null;

  const start = range[0];
  const end = range[1];

  const startDate = new Date(start.getFullYear(), start.getMonth(), start.getDate());
  const endDate = new Date(end.getFullYear(), end.getMonth(), end.getDate());

  const sameDay = startDate.getTime() === endDate.getTime();

  // Single-day requests can optionally be hour-based.
  if (sameDay) {
    if (!isWeekday(startDate)) return 0;
    if (!includeHours) return WORKDAY_HOURS;

    if (isInvalidTimeRange(startTime, endTime)) return null;

    const hours = diffHours(startTime, endTime);
    if (hours == null) return null;

    // Cap a single day's time-off to one workday.
    const clampedHours = clamp(hours, 0, WORKDAY_HOURS);
    return Math.round(clampedHours * 4) / 4;
  }

  let total = 0;
  for (let d = new Date(startDate); d <= endDate; d.setDate(d.getDate() + 1)) {
    if (!isWeekday(d)) continue;
    total += WORKDAY_HOURS;
  }

  // Keep to quarter-hour granularity for display/persistence.
  return Math.round(total * 4) / 4;
};

export default function TimeOffRequestForm({ initialValue, onSubmit, submitLabel = 'Submit', loading = false, error }) {
  const formRef = useRef();
  const [manualTotalHours, setManualTotalHours] = useState(false);
  const [timeError, setTimeError] = useState(null);

  const [formValue, setFormValue] = useState(() => {
    const start = initialValue?.start_date ? new Date(initialValue.start_date) : null;
    const end = initialValue?.end_date ? new Date(initialValue.end_date) : null;
    const initialTotalHours = initialValue?.total_hours;

    const startMinutes = initialValue?.start_minutes ?? null;
    const endMinutes = initialValue?.end_minutes ?? null;
    const hasMinutes = startMinutes != null && endMinutes != null;

    const startTime = hasMinutes ? timeFromMinutes(startMinutes) : makeTime(9, 0);
    const endTime = hasMinutes ? timeFromMinutes(endMinutes) : makeTime(17, 0);

    return {
      request_type: initialValue?.request_type || DEFAULT_REQUEST_TYPE,
      date_range: start && end ? [start, end] : null,
      notes: initialValue?.notes || '',
      include_hours: !!hasMinutes,
      start_time: snapMinutesToQuarterHour(startTime),
      end_time: snapMinutesToQuarterHour(endTime),
      total_hours: coerceFiniteNumber(initialTotalHours, null),
    };
  });

  useEffect(() => {
    setTimeError(null);

    const range = formValue.date_range;
    const start = range?.[0] || null;
    const end = range?.[1] || null;
    const singleDay = !!start && !!end && isSameDay(start, end);

    // Hour selection is only supported for single-day requests.
    if (!singleDay && formValue.include_hours) {
      setFormValue((prev) => ({ ...prev, include_hours: false }));
    }

    if (singleDay && formValue.include_hours) {
      if (isInvalidTimeRange(formValue.start_time, formValue.end_time)) {
        setTimeError('End time must be after the start time');
      }
    }

    const computed = calculateTotalWorkHours({
      range: range,
      includeHours: singleDay && !!formValue.include_hours,
      startTime: formValue.start_time,
      endTime: formValue.end_time,
    });

    // If no range, clear any previously computed/entered hours.
    if (!range || !range[0] || !range[1]) {
      if (formValue.total_hours !== null) {
        setFormValue((prev) => ({ ...prev, total_hours: null }));
      }
      if (manualTotalHours) setManualTotalHours(false);
      return;
    }

    if (!manualTotalHours) {
      if (!Object.is(formValue.total_hours, computed)) {
        setFormValue((prev) => ({ ...prev, total_hours: computed }));
      }
    }
  }, [
    formValue.date_range,
    formValue.include_hours,
    formValue.start_time,
    formValue.end_time,
    formValue.total_hours,
    manualTotalHours,
  ]);

  const handleSubmit = async () => {
    const range = formValue.date_range;
    if (!range || !range[0] || !range[1]) return;

    const singleDay = isSameDay(range[0], range[1]);
    if (singleDay && formValue.include_hours) {
      if (isInvalidTimeRange(formValue.start_time, formValue.end_time)) {
        setTimeError('End time must be after the start time');
        return;
      }
    }

    const pad2 = (n) => String(n).padStart(2, '0');
    const formatLocalDateOnly = (date) => {
      // Timezone-agnostic date-only formatter (no UTC conversion)
      return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
    };

    await onSubmit?.({
      request_type: DEFAULT_REQUEST_TYPE,
      start_date: formatLocalDateOnly(range[0]),
      end_date: formatLocalDateOnly(range[1]),
      notes: formValue.notes || null,
      // Coerced here as well as on blur: submitting without leaving the field
      // would otherwise send it as typed.
      total_hours: coerceFiniteNumber(formValue.total_hours, null),
      start_minutes: singleDay && formValue.include_hours ? minutesSinceMidnight(formValue.start_time) : null,
      end_minutes: singleDay && formValue.include_hours ? minutesSinceMidnight(formValue.end_time) : null,
    });
  };

  return (
    <div>
      {error ? (
        <Message type="error" showIcon header="Error" style={{ marginBottom: 12 }}>
          {String(error?.message || error)}
        </Message>
      ) : null}

      <Form ref={formRef} fluid formValue={formValue} onChange={setFormValue}>

        <Form.Group controlId="date_range">
          <Form.ControlLabel>Date Range</Form.ControlLabel>
          <DateRangePicker
            value={formValue.date_range}
            onChange={(v) => {
              const singleDay = !!v?.[0] && !!v?.[1] && isSameDay(v[0], v[1]);
              setFormValue((prev) => ({
                ...prev,
                date_range: v,
                include_hours: singleDay ? prev.include_hours : false,
              }));
              setManualTotalHours(false);
            }}
            isoWeek={false}
            weekStart={0}
            format="MM/dd/yyyy"
            style={{ width: '100%' }}
            placeholder="Select start and end"
          />
        </Form.Group>

        {(() => {
          const start = formValue.date_range?.[0] || null;
          const end = formValue.date_range?.[1] || null;
          const singleDay = !!start && !!end && isSameDay(start, end);
          return singleDay ? (
            <Form.Group controlId="include_hours">
              <div className="d-flex justify-content-between align-items-center">
                <Form.ControlLabel className="m-0">Include Hours</Form.ControlLabel>
                <Toggle
                  checked={!!formValue.include_hours}
                  onChange={(checked) => {
                    setFormValue((prev) => ({
                      ...prev,
                      include_hours: checked,
                      start_time: checked ? (prev.start_time || makeTime(9, 0)) : prev.start_time,
                      end_time: checked ? (prev.end_time || makeTime(17, 0)) : prev.end_time,
                    }));
                    setManualTotalHours(false);
                  }}
                />
              </div>

              {formValue.include_hours ? (
                <div className="d-flex align-items-center gap-2 flex-wrap mt-2">
                  <div style={{ minWidth: 220 }}>
                    <div className="text-muted" style={{ fontSize: 12 }}>Start time</div>
                    <div className="d-flex align-items-center gap-2 flex-wrap">
                      {(() => {
                        const parts = getTimeParts12h(formValue.start_time);
                        const hourOptions = [
                          ...Array.from({ length: 11 }, (_, i) => ({ label: String(i + 1), value: i + 1 })),
                          { label: '12', value: 12 }
                        ];
                        const minuteOptions = [0, 15, 30, 45].map((m) => ({ label: String(m).padStart(2, '0'), value: m }));
                        const meridiemOptions = ['AM', 'PM'].map((m) => ({ label: m, value: m }));

                        return (
                          <>
                            <SelectPicker
                              data={hourOptions}
                              value={parts.hour12}
                              cleanable={false}
                              searchable={false}
                              style={{ width: 90 }}
                              onChange={(hour12) => {
                                setTimeError(null);
                                setFormValue((prev) => ({
                                  ...prev,
                                  start_time: setTimeParts(prev.start_time, {
                                    hour12,
                                    minutes: getTimeParts12h(prev.start_time).minutes,
                                    meridiem: getTimeParts12h(prev.start_time).meridiem,
                                  }),
                                }));
                                setManualTotalHours(false);
                              }}
                            />
                            <SelectPicker
                              data={minuteOptions}
                              value={parts.minutes - (parts.minutes % 15)}
                              cleanable={false}
                              searchable={false}
                              style={{ width: 90 }}
                              onChange={(minutes) => {
                                setTimeError(null);
                                setFormValue((prev) => ({
                                  ...prev,
                                  start_time: setTimeParts(prev.start_time, {
                                    hour12: getTimeParts12h(prev.start_time).hour12,
                                    minutes,
                                    meridiem: getTimeParts12h(prev.start_time).meridiem,
                                  }),
                                }));
                                setManualTotalHours(false);
                              }}
                            />
                            <SelectPicker
                              data={meridiemOptions}
                              value={parts.meridiem}
                              cleanable={false}
                              searchable={false}
                              style={{ width: 90 }}
                              onChange={(meridiem) => {
                                setTimeError(null);
                                setFormValue((prev) => ({
                                  ...prev,
                                  start_time: setTimeParts(prev.start_time, {
                                    hour12: getTimeParts12h(prev.start_time).hour12,
                                    minutes: getTimeParts12h(prev.start_time).minutes,
                                    meridiem,
                                  }),
                                }));
                                setManualTotalHours(false);
                              }}
                            />
                          </>
                        );
                      })()}
                    </div>
                  </div>

                  <div style={{ minWidth: 220 }}>
                    <div className="d-flex align-items-center justify-content-between">
                      <div className="text-muted" style={{ fontSize: 12 }}>End time</div>
                      {isInvalidTimeRange(formValue.start_time, formValue.end_time) ? (
                        <Whisper
                          placement="top"
                          trigger="hover"
                          speaker={<Tooltip>{timeError || 'End time must be after the start time.'}</Tooltip>}
                        >
                          <span className="text-warning" style={{ fontSize: 12, cursor: 'help' }}>
                            <i className="bi bi-exclamation-triangle-fill" />
                          </span>
                        </Whisper>
                      ) : null}
                    </div>

                    <div className="d-flex align-items-center gap-2 flex-wrap">
                      {(() => {
                        const parts = getTimeParts12h(formValue.end_time);
                        const hourOptions = [
                          ...Array.from({ length: 11 }, (_, i) => ({ label: String(i + 1), value: i + 1 })),
                          { label: '12', value: 12 }
                        ];
                        const minuteOptions = [0, 15, 30, 45].map((m) => ({ label: String(m).padStart(2, '0'), value: m }));
                        const meridiemOptions = ['AM', 'PM'].map((m) => ({ label: m, value: m }));

                        return (
                          <>
                            <SelectPicker
                              data={hourOptions}
                              value={parts.hour12}
                              cleanable={false}
                              searchable={false}
                              style={{ width: 90 }}
                              onChange={(hour12) => {
                                setFormValue((prev) => ({
                                  ...prev,
                                  end_time: setTimeParts(prev.end_time, {
                                    hour12,
                                    minutes: getTimeParts12h(prev.end_time).minutes,
                                    meridiem: getTimeParts12h(prev.end_time).meridiem,
                                  }),
                                }));
                                setManualTotalHours(false);
                              }}
                            />
                            <SelectPicker
                              data={minuteOptions}
                              value={parts.minutes - (parts.minutes % 15)}
                              cleanable={false}
                              searchable={false}
                              style={{ width: 90 }}
                              onChange={(minutes) => {
                                setFormValue((prev) => ({
                                  ...prev,
                                  end_time: setTimeParts(prev.end_time, {
                                    hour12: getTimeParts12h(prev.end_time).hour12,
                                    minutes,
                                    meridiem: getTimeParts12h(prev.end_time).meridiem,
                                  }),
                                }));
                                setManualTotalHours(false);
                              }}
                            />
                            <SelectPicker
                              data={meridiemOptions}
                              value={parts.meridiem}
                              cleanable={false}
                              searchable={false}
                              style={{ width: 90 }}
                              onChange={(meridiem) => {
                                setFormValue((prev) => ({
                                  ...prev,
                                  end_time: setTimeParts(prev.end_time, {
                                    hour12: getTimeParts12h(prev.end_time).hour12,
                                    minutes: getTimeParts12h(prev.end_time).minutes,
                                    meridiem,
                                  }),
                                }));
                                setManualTotalHours(false);
                              }}
                            />
                          </>
                        );
                      })()}
                    </div>
                  </div>
                </div>
              ) : null}
            </Form.Group>
          ) : null;
        })()}

        <Form.Group controlId="total_hours">
          <Form.ControlLabel>Total work hours (weekdays only)</Form.ControlLabel>
          <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
            <div className="d-flex align-items-center gap-2 flex-wrap">
              <div style={{ minWidth: 220 }}>
                <div className="text-muted" style={{ fontSize: 12 }}>Auto-calculated (default {WORKDAY_HOURS}h per weekday)</div>
                <InputNumber
                  min={0}
                  max={10000}
                  step={0.25}
                  value={formValue.total_hours ?? null}
                  // Kept as typed. Coercing per keystroke turns "5." into 5 and
                  // feeds that back into the controlled input, which deletes the
                  // decimal point the moment it is typed — half hours could
                  // never be entered. Normalised on blur instead; the payload
                  // and the server both coerce anyway.
                  onChange={(v) => {
                    setManualTotalHours(true);
                    setFormValue((prev) => ({ ...prev, total_hours: v === '' || v == null ? null : v }));
                  }}
                  onBlur={() => setFormValue((prev) => ({
                    ...prev,
                    total_hours: coerceFiniteNumber(prev.total_hours, null),
                  }))}
                  placeholder="Auto"
                  style={{ width: '100%' }}
                />
              </div>

              <ButtonGroup size="xs">
                <Button
                  appearance="subtle"
                  disabled={!manualTotalHours}
                  onClick={() => {
                    setManualTotalHours(false);
                    const computed = calculateTotalWorkHours({
                      range: formValue.date_range,
                      includeHours: !!formValue.include_hours && !!formValue.date_range?.[0] && !!formValue.date_range?.[1] && isSameDay(formValue.date_range[0], formValue.date_range[1]),
                      startTime: formValue.start_time,
                      endTime: formValue.end_time,
                    });
                    setFormValue((prev) => ({ ...prev, total_hours: computed }));
                  }}
                >
                  Reset to Auto
                </Button>
              </ButtonGroup>
            </div>
          </div>
        </Form.Group>

        <Form.Group controlId="notes">
          <Form.ControlLabel>Notes</Form.ControlLabel>
          <Form.Control name="notes" accepter={Textarea} rows={3} />
        </Form.Group>

        <div className="d-flex justify-content-end mt-3">
          <Button appearance="primary" loading={loading} onClick={handleSubmit}>
            {submitLabel}
          </Button>
        </div>
      </Form>
    </div>
  );
}
